// src/context/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { login as apiLogin, fetchRolesPermissions } from '../utils/api'; // Import your actual API functions
import { User, AuthContextType } from '../utils/types'; // Import your defined types

// Create context with the correct type, initialized as undefined
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [loadingAuth, setLoadingAuth] = useState<boolean>(true); // State to track initial auth check

    // Effect to check authentication status on initial load
    useEffect(() => {
        const token = localStorage.getItem('token');
        const storedUserJson = localStorage.getItem('user');
        if (token && storedUserJson) {
            try {
                const storedUser: User = JSON.parse(storedUserJson);
                // You might want to add a check here to verify the token with your backend
                // For now, we'll assume the stored token/user is valid if present
                setUser(storedUser);
                setIsAuthenticated(true);
            } catch (error) {
                console.error("Failed to parse stored user:", error);
                // Clear storage if data is invalid
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                localStorage.removeItem('customer');
                localStorage.removeItem('rolesPermissions');
            }
        }
        setLoadingAuth(false); // Mark initial auth check as complete
    }, []);

    // Login function implementation
    const login = useCallback(async (email: string, password: string): Promise<User> => {
        try {
            // Call your API login function
            const response = await apiLogin(email, password);

            // --- Adjust based on your actual API response structure ---
            const loggedInUser: User = response.data.user;
            const customerData = response.data.customer; // If separate
            // ---

            if (!loggedInUser || !loggedInUser.remember_token) {
                throw new Error("Login response missing user data or token.");
            }

            // Store token and user data in localStorage
            localStorage.setItem('token', loggedInUser.remember_token);
            localStorage.setItem('user', JSON.stringify(loggedInUser));
            if (customerData) {
                localStorage.setItem('customer', JSON.stringify(customerData));
            }

            // Optionally fetch and store roles/permissions immediately after login
            try {
                const rolesPermissionsResponse = await fetchRolesPermissions();
                localStorage.setItem('rolesPermissions', JSON.stringify(rolesPermissionsResponse));
            } catch (permError) {
                console.warn("Failed to fetch roles/permissions after login:", permError);
                // Decide how to handle this - maybe proceed without them?
            }

            // Update context state
            setUser(loggedInUser);
            setIsAuthenticated(true);

            return loggedInUser; // Return user data for immediate use (e.g., redirection)

        } catch (error) {
            // Ensure state is cleared on failed login
            logout(); // Use logout function to clean up
            console.error("AuthContext login failed:", error);
            throw error; // Re-throw error for the component to handle (e.g., show message)
        }
    }, []); // Add `logout` to dependency array if it's defined outside useCallback or changes

    // Logout function implementation
    const logout = useCallback(() => {
        setUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('customer');
        localStorage.removeItem('rolesPermissions');
        // You might want to redirect to login page here or call a backend logout endpoint
        // navigate('/login'); // If using useNavigate hook here
    }, []);

    // Render loading indicator or null while checking auth, prevents flicker
    if (loadingAuth) {
        return <div>Loading...</div>; // Or your preferred loading component
    }

    // Provide the state and functions to children
    return (
        <AuthContext.Provider value={{ isAuthenticated, user, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => { // Ensure hook returns the correct type
    const context = useContext(AuthContext);
    if (context === undefined) { // Check for undefined, not !context
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
